import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import '../../scss/_root-component.scss';
import { useSetResolved } from 'src/core/engine/router/index';
import { useDeps } from 'src/hooks/useDeps';
import { absoluteUrl } from 'src/util/absoluteUrl';
import { ErrorComponent } from 'src/components/Error/ErrorComponent';
import { NotFound } from 'src/components/NotFound/NotFound';
import { cleanBundleProduct, cleanWearerSummary } from 'src/components/ProductDetail/utils/product-detail.types';
import { useHeadTags, escapeAttr } from 'src/core/engine/head';
import { currentUrl } from 'src/util/currentUrl';
import {
    wearerBundleDetail,
    wearerBundleDetailVariables,
    wearerBundleDetail_wearerBundleDetail,
} from 'src/queries/__generated__/wearerBundleDetail';
import { queryToWearerOptionsMap } from 'src/components/WeareBundleProduct/utils/wearer.types';
import invariant from 'tiny-invariant';
import { Container, Wrapper } from 'src/components/Layout';
import wearerBundleProductQuery from 'src/queries/wearerBundleProductDetail.graphql';
import { TreatmentWithConfig } from 'src/types/global-types';
import { ModalWearerBundle } from 'src/components/WeareBundleProduct/WearerBundleOptions';
import { BundlePageSkeleton } from 'src/elements/Skeletons/Skeletons';
import { useWeareBundlePrices } from 'src/hooks/useWeareBundlePrices';
import { useBundleCustomisationData } from 'src/components/BundleProduct/hooks/useBundleCustomisationData';
import Bugsnag from '@bugsnag/js';
import wearerBundleDifferenceQuery from 'src/queries/wearerBundleDifference.graphql';
import { MixAndMatchBundleRoot } from 'src/MixAndMatchBundle/MixAndMatchBundleRoot';
import { useIndexSku } from 'src/hooks/useIndexSku';
import { KEYWORDS } from 'src/constants/meta';
import { useKlaviyoViewedBundle } from 'src/features/klaviyo/hooks/useKlaviyoViewedBundle';
import { useSetPopupPageProduct } from 'src/components/PopupBanner/PopupPageContext';

type Props = {
    pathname: string;
    id: number;
    treatmentWithConfig: TreatmentWithConfig;
};

type WearerBundleProductProps = {
    id: number;
    first: wearerBundleDetail_wearerBundleDetail;
    customisationOption: string;
};

export const WearerBundleRoot: React.FC<Props> = (props) => {
    const { loading, data } = useQuery<wearerBundleDetail, wearerBundleDetailVariables>(wearerBundleDifferenceQuery, {
        variables: { ids: [props.id], noAuthorization: true },
        ssr: useDeps().env.SSR_GQL,
    });

    if (loading) {
        return (
            <Wrapper variant="page-inner">
                <Container>
                    <BundlePageSkeleton />
                </Container>
            </Wrapper>
        );
    }

    const first = data?.wearerBundleDetail[0];
    const isBundleMixAndMatch = Boolean(first?.bundle_items?.find((item) => item.mixed === true));
    if (isBundleMixAndMatch) {
        return <MixAndMatchBundleRoot {...props} />;
    } else {
        return <WearerBundleProductRoot {...props} />;
    }
};

export const WearerBundleProductRoot: React.FC<Props> = ({ id }) => {
    useSetResolved();
    /**
     * Make the initial Product Query
     */
    const { loading, error, data } = useQuery<wearerBundleDetail, wearerBundleDetailVariables>(
        wearerBundleProductQuery,
        {
            variables: { ids: [id], noAuthorization: true },
            ssr: useDeps().env.SSR_GQL,
        },
    );

    const customisationOption = useBundleCustomisationData(data?.['wearerBundleDetail']?.[0]?.logo_policy?.toString());

    const first = data?.wearerBundleDetail?.[0];
    const hasCompleteData = data && Array.isArray(data.wearerBundleDetail) && first;

    if (loading || !hasCompleteData)
        return (
            <Wrapper variant="page-inner">
                <Container>
                    <BundlePageSkeleton />
                </Container>
            </Wrapper>
        );

    if (error) {
        return <ErrorComponent message={error?.message} />;
    }

    if (!data) {
        Bugsnag.notify('!data returned true');
        return <NotFound />;
    }

    if (!Array.isArray(data.wearerBundleDetail)) {
        Bugsnag.notify('!Array.isArray(data.wearerBundleDetail) returned true');
        return <NotFound />;
    }

    if (!first) {
        Bugsnag.notify('!first returned true');
        return <NotFound />;
    }

    return <WearerBundleProduct first={first} id={id} customisationOption={customisationOption} />;
};

const WearerBundleProduct: React.FC<WearerBundleProductProps> = ({ first, id, customisationOption }) => {
    const product = cleanBundleProduct(first);
    const { DOMAIN } = useDeps().env;
    useSetPopupPageProduct(product.id, product.categories?.[0]?.id ?? null);
    const prices = useWeareBundlePrices(id);

    const url = currentUrl(DOMAIN!, `/${product.url_key}` || '');

    useKlaviyoViewedBundle({ product, price: prices, url });

    invariant(first.__typename === 'WearerBundlesProduct', 'product type should be WearerBundlesProduct');
    invariant(Array.isArray(first.bundle_items), 'product type should be WearerBundlesProduct');

    const productName = product.name;
    const title = productName.toLowerCase().includes('workwear express')
        ? productName
        : `${productName} | Workwear Express`;

    const noIndexedProduct = useIndexSku(product.sku);

    const WearerBundleComponent = () => {
        const wearerOptions = queryToWearerOptionsMap(first);
        const summary = cleanWearerSummary(first.bundle_items);

        return (
            <ModalWearerBundle
                wearerOptions={wearerOptions}
                product={product}
                summary={summary}
                prices={prices}
                customisationOption={customisationOption}
            />
        );
    };

    const { setHead } = useHeadTags();

    useEffect(() => {
        const desc = product.meta_description || product.meta_title || product.name || '';
        const meta = [
            `<meta name="description" content="${escapeAttr(desc)}" />`,
            noIndexedProduct ? '<meta name="robots" content="noindex" />' : '',
            `<meta property="og:title" content="${escapeAttr(product.name || '')}" />`,
            `<meta property="og:description" content="${escapeAttr(desc)}" />`,
            `<meta property="og:image" content="${escapeAttr(absoluteUrl(DOMAIN!, product.small_image || ''))}" />`,
            `<meta name="keywords" content="${escapeAttr(product.meta_keywords || KEYWORDS)}" />`,
        ].join('');
        const link = `<link rel="canonical" href="${escapeAttr(url)}" />`;
        setHead({ title: `<title>${escapeAttr(title)}</title>`, meta, link });
    }, [title, product, url, noIndexedProduct, setHead, DOMAIN]);

    return (
        <Wrapper variant="page-inner" bg="white">
            <WearerBundleComponent />
        </Wrapper>
    );
};

export default WearerBundleRoot;
